import { useEffect } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/classNames";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  isPending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        if (!isPending) onCancel();
      }
    }
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, isPending, onCancel]);

  if (!open) return null;

  const titleId = "confirm-dialog-title";
  const descriptionId = description ? "confirm-dialog-description" : undefined;

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="presentation"
      className="pointer-events-auto fixed inset-0 z-150 flex items-center justify-center bg-[rgba(10,10,10,0.62)] px-4 py-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isPending) {
          onCancel();
        }
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={cn(
          "relative w-full max-w-md p-6 sm:p-8",
          "border-[3px] border-neutral-950 bg-[#fffaf1]",
          "shadow-[12px_12px_0_rgba(19,19,19,0.18)]",
        )}
      >
        <h2
          id={titleId}
          className="text-[24px] leading-tight font-['Fondamento','Palatino_Linotype',serif] text-[#23443a]"
        >
          {title}
        </h2>
        {description && (
          <p
            id={descriptionId}
            className="mt-3 text-[14px] leading-relaxed text-neutral-800 font-['Segoe_UI',sans-serif]"
          >
            {description}
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <Button
            autoFocus
            variant="cancel"
            chrome="compact"
            onClick={onCancel}
            disabled={isPending}
            className="min-w-0 px-3 py-2"
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "primary"}
            chrome="compact"
            onClick={onConfirm}
            disabled={isPending}
            className="min-w-0 px-3 py-2"
          >
            {isPending ? "Working..." : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
